/**
 * Core InlineEdit Component
 *
 * Click-to-edit text field that toggles between display and edit mode.
 * Used for editable titles, names, descriptions in tables and detail pages.
 *
 * @example
 *   <InlineEdit value={name} onSave={(val) => updateName(val)} placeholder="Untitled" />
 *   <InlineEdit value={desc} onSave={save} multiline maxLength={200} />
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, TextField } from '@mui/material';
import { Check, X, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface InlineEditProps {
  /** Current value */
  value: string;
  /** Save handler (Enter or check button) */
  onSave: (value: string) => void | Promise<void>;
  /** Cancel handler */
  onCancel?: () => void;
  /** Placeholder when empty */
  placeholder?: string;
  /** Multiline textarea mode */
  multiline?: boolean;
  /** Max character length */
  maxLength?: number;
  /** Disabled */
  disabled?: boolean;
  /** Required — prevent saving empty */
  required?: boolean;
  /** Text variant for display mode */
  variant?: 'body1' | 'body2' | 'h6' | 'subtitle1' | 'subtitle2';
  /** Font weight for display mode */
  fontWeight?: number;
  className?: string;
}

export const InlineEdit: React.FC<InlineEditProps> = ({
  value,
  onSave,
  onCancel,
  placeholder = 'Click to edit',
  multiline = false,
  maxLength,
  disabled = false,
  required = false,
  variant = 'body2',
  fontWeight = 500,
  className = '',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [editing, value]);

  const handleSave = useCallback(async () => {
    const trimmed = draft.trim();
    if (required && !trimmed) return;
    if (trimmed === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }, [draft, value, onSave, required]);

  const handleCancel = () => {
    setDraft(value);
    setEditing(false);
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') handleCancel();
  };

  if (editing) {
    return (
      <Box className={cn('flex items-start gap-1', className)}>
        <TextField
          inputRef={inputRef}
          value={draft}
          onChange={(e) => setDraft(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (!saving) handleCancel(); }}
          size="small"
          multiline={multiline}
          rows={multiline ? 2 : undefined}
          fullWidth
          disabled={saving}
          inputProps={{ maxLength }}
          sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
        />
        <IconButton size="small" onClick={handleSave} disabled={saving} color="primary" onMouseDown={(e) => e.preventDefault()}>
          <Check className="h-4 w-4" />
        </IconButton>
        <IconButton size="small" onClick={handleCancel} disabled={saving} onMouseDown={(e) => e.preventDefault()}>
          <X className="h-4 w-4" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      className={cn('group inline-flex items-center gap-1 cursor-pointer rounded px-1 -mx-1 hover:bg-slate-50 dark:hover:bg-slate-800/50', disabled && 'cursor-default hover:bg-transparent', className)}
      onClick={!disabled ? () => setEditing(true) : undefined}
    >
      <Typography
        variant={variant}
        fontWeight={fontWeight}
        color={value ? 'text.primary' : 'text.secondary'}
        sx={{ fontStyle: !value ? 'italic' : 'normal' }}
      >
        {value || placeholder}
      </Typography>
      {!disabled && (
        <Pencil className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </Box>
  );
};
export default InlineEdit;
